import {
  onBeforeUnmount,
  onMounted,
} from 'vue';

import {
  EKeyboardKey,
} from '../constants/keyboard.ts';
import {
  EKeyboardArea,
  KEYBOARD_AREA_ATTRIBUTE,
  activeKeyboardArea,
  findKeyboardArea,
  findKeyboardAreaElement,
} from '../utils/keyboard-area.ts';
import {
  isEditableElement,
} from '../utils/keyboard-target.ts';

// `KeyboardEvent.code` names the physical key, so the digits are found on every layout: with ⌥ held,
// macOS reports `¡` as `key` for the 1, and AZERTY layouts need Shift for the digits.
const DIGIT_CODE_TO_KEYBOARD_AREA: Record<string, EKeyboardArea> = {
  Digit1: EKeyboardArea.AppNavigation,
  Digit2: EKeyboardArea.PageNavigation,
  Digit3: EKeyboardArea.PageContent,
};

const FOCUSABLE_SELECTOR = 'a[href], button:not(:disabled), [tabindex]:not([tabindex="-1"])';
// The open app in the navigation bar, the selected page of a menu or the selected node of a tree
const CURRENT_ENTRY_SELECTOR = '[aria-current]:not([aria-current="false"]), [role="treeitem"][aria-selected="true"]';
// The entries of a navigation come before its buttons, e.g. the one collapsing a tree sidebar.
const ENTRY_SELECTOR = '[role="treeitem"], [role="menuitem"], a[href]';
const HIGHLIGHT_CLASS = 'keyboard-area-highlight';
const HIGHLIGHT_VISIBLE_CLASS = `${HIGHLIGHT_CLASS}_visible`;

/**
 * The area of Alt/⌥ + 1, 2 or 3 alone: Ctrl + Alt is AltGr on Windows, and Shift + Alt switches the
 * keyboard layout. The `AltGraph` modifier state must not be checked, as Firefox on macOS reports it for every ⌥.
 */
function findShortcutArea(event: KeyboardEvent): EKeyboardArea | undefined {
  const hasOnlyAlt = event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey;
  return hasOnlyAlt
    ? DIGIT_CODE_TO_KEYBOARD_AREA[event.code]
    : undefined;
}

/**
 * Switches the keyboard between the app navigation, the page navigation and the content with
 * Alt/⌥ + 1, 2 and 3. The switch moves the focus into the area, so that its keys, e.g. the arrow
 * keys of a tree, apply to it, and highlights the area while Alt/⌥ is held. Areas are marked with
 * `data-keyboard-area`.
 */
export function useKeyboardAreas() {
  const areaElementToLastFocusedElement = new WeakMap<HTMLElement, HTMLElement>();
  let highlightElement: HTMLElement | undefined;

  function canTakeFocus(element: HTMLElement | null | undefined): element is HTMLElement {
    return !!element && element.isConnected && element.checkVisibility();
  }

  // Never a text field: the keys pressed after the switch would type into it or move its caret
  // instead of moving through the area.
  function findElementToFocus(areaElement: HTMLElement, area: EKeyboardArea): HTMLElement | undefined {
    const lastFocusedElement = areaElementToLastFocusedElement.get(areaElement);
    if (canTakeFocus(lastFocusedElement) && areaElement.contains(lastFocusedElement) && !isEditableElement(lastFocusedElement)) {
      return lastFocusedElement;
    }
    // The content has no current entry, and its first control is rarely where the user wants to
    // start, so the keys go to the content as a whole, e.g. to its only table.
    if (area === EKeyboardArea.PageContent) {
      return undefined;
    }
    const currentEntry = areaElement.querySelector<HTMLElement>(CURRENT_ENTRY_SELECTOR);
    if (canTakeFocus(currentEntry)) {
      return currentEntry;
    }
    const firstEntry = Array.from(areaElement.querySelectorAll<HTMLElement>(ENTRY_SELECTOR))
      .find((entry) => canTakeFocus(entry));
    if (firstEntry) {
      return firstEntry;
    }
    return Array.from(areaElement.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
      .find((element) => canTakeFocus(element) && !isEditableElement(element));
  }

  function focusArea(areaElement: HTMLElement, area: EKeyboardArea) {
    const elementToFocus = findElementToFocus(areaElement, area);
    elementToFocus?.focus();
    // An element may refuse the focus, e.g. an entry that is not focusable.
    if (elementToFocus && document.activeElement === elementToFocus) {
      return;
    }
    // The entries of a menu are not focusable, the menu itself takes the focus and marks one entry as focused.
    const focusableAncestor = elementToFocus?.parentElement?.closest<HTMLElement>(FOCUSABLE_SELECTOR);
    if (focusableAncestor && areaElement.contains(focusableAncestor)) {
      focusableAncestor.focus();
      if (document.activeElement === focusableAncestor) {
        return;
      }
    }
    if (!areaElement.hasAttribute('tabindex')) {
      areaElement.tabIndex = -1;
    }
    areaElement.focus();
  }

  function createHighlightElement(): HTMLElement {
    const element = document.createElement('div');
    element.className = HIGHLIGHT_CLASS;
    element.setAttribute('aria-hidden', 'true');
    document.body.append(element);
    return element;
  }

  /**
   * The part of the screen that the area takes. The content contains the page navigation, which
   * pages place on its left side, so the content starts to the right of it.
   */
  function getAreaRectangle(areaElement: HTMLElement): DOMRect {
    const areaRectangle = areaElement.getBoundingClientRect();
    const nestedAreaElement = Array.from(areaElement.querySelectorAll<HTMLElement>(`[${KEYBOARD_AREA_ATTRIBUTE}]`))
      .find((element) => element.checkVisibility());
    if (!nestedAreaElement) {
      return areaRectangle;
    }
    const left = Math.max(areaRectangle.left, nestedAreaElement.getBoundingClientRect().right);
    return new DOMRect(left, areaRectangle.top, areaRectangle.right - left, areaRectangle.height);
  }

  function highlightArea(areaElement: HTMLElement) {
    highlightElement ??= createHighlightElement();
    const areaRectangle = getAreaRectangle(areaElement);
    highlightElement.style.top = `${areaRectangle.top}px`;
    highlightElement.style.left = `${areaRectangle.left}px`;
    highlightElement.style.width = `${areaRectangle.width}px`;
    highlightElement.style.height = `${areaRectangle.height}px`;
    highlightElement.classList.add(HIGHLIGHT_VISIBLE_CLASS);
  }

  function releaseHighlight() {
    highlightElement?.classList.remove(HIGHLIGHT_VISIBLE_CLASS);
  }

  function switchAreaOnShortcut(event: KeyboardEvent) {
    const area = findShortcutArea(event);
    if (!area) {
      return;
    }
    // Also when the area does not exist on the page, so that ⌥ does not type a character instead.
    event.preventDefault();
    const areaElement = findKeyboardAreaElement(area);
    if (event.repeat || !areaElement) {
      return;
    }
    focusArea(areaElement, area);
    activeKeyboardArea.value = area;
    highlightArea(areaElement);
  }

  function releaseHighlightOnAlt(event: KeyboardEvent) {
    if (event.key === EKeyboardKey.Alt) {
      releaseHighlight();
    }
  }

  function rememberFocusedElement(event: FocusEvent) {
    if (!(event.target instanceof HTMLElement)) {
      return;
    }
    activeKeyboardArea.value = findKeyboardArea(event.target);
    const areaElement = event.target.closest<HTMLElement>(`[${KEYBOARD_AREA_ATTRIBUTE}]`);
    if (areaElement && areaElement !== event.target) {
      areaElementToLastFocusedElement.set(areaElement, event.target);
    }
  }

  function rememberClickedArea(event: PointerEvent) {
    if (event.target instanceof Element) {
      activeKeyboardArea.value = findKeyboardArea(event.target);
    }
  }

  // The shortcut in the capture phase of the window, so that no component, e.g. an open dropdown,
  // stops it. The keyup of Alt never arrives when the window loses the focus while it is held, e.g. on Alt + Tab.
  onMounted(() => {
    window.addEventListener('keydown', switchAreaOnShortcut, true);
    window.addEventListener('keyup', releaseHighlightOnAlt, true);
    window.addEventListener('blur', releaseHighlight);
    document.addEventListener('focusin', rememberFocusedElement);
    document.addEventListener('pointerdown', rememberClickedArea, true);
  });
  onBeforeUnmount(() => {
    window.removeEventListener('keydown', switchAreaOnShortcut, true);
    window.removeEventListener('keyup', releaseHighlightOnAlt, true);
    window.removeEventListener('blur', releaseHighlight);
    document.removeEventListener('focusin', rememberFocusedElement);
    document.removeEventListener('pointerdown', rememberClickedArea, true);
    highlightElement?.remove();
  });
}
